import { formatCurrency, formatPercentage } from '@if/core/utils'
import { BodyText, Caption, DisplayValue, FieldSectionTitle } from '@if/mui'
import { Divider, Stack } from '@mui/material'
import { formatComputed } from '@src/data/worksheet-selectors'
import { ProductsComputeResultDto } from '@src/types/ProductsComputeResultDto'
import { useTranslation } from 'react-i18next'
import WorksheetFundingSummaryCard from '../../components/WorksheetFundingSummaryCard'

interface Props {
  computedFunding: ProductsComputeResultDto | null
  isFetching?: boolean
  isAmountOutOfRange: boolean
}

const ProductsLoanFundingSummary = ({ computedFunding, isFetching = false, isAmountOutOfRange }: Props) => {
  const { t } = useTranslation()
  const showAnnualFees = (computedFunding?.annualFees ?? 0) > 0

  const isReady = Boolean(computedFunding) || isAmountOutOfRange

  const body = isReady ? (
    <Stack spacing={2} divider={<Divider />}>
      <Stack spacing={0.5}>
        <FieldSectionTitle>{t('calculator.monthlyPayment')}</FieldSectionTitle>
        <DisplayValue>
          {formatComputed(computedFunding, (funding) => formatCurrency(funding.paymentAmountForFrequency))}
        </DisplayValue>
      </Stack>
      {computedFunding && (
        <Stack spacing={1}>
          <BodyText>
            {t('calculator.totalInterestCosts', { rate: formatPercentage(computedFunding.interestRate) })}
          </BodyText>
          <Caption>{t('calculator.effectiveRate', { effectiveRate: computedFunding.effectiveRate })}</Caption>
          {showAnnualFees && (
            <Caption>{t('calculator.annualFees', { annualFees: computedFunding.annualFees })}</Caption>
          )}
        </Stack>
      )}
      <Caption>{t('worksheet.fundingSummaryFootnote')}</Caption>
    </Stack>
  ) : null

  return (
    <WorksheetFundingSummaryCard
      title={t('worksheet.fundingSummary')}
      emptyMessage={isFetching ? t('common.loading') : t('calculator.detailsAvailable')}
      isReady={isReady}
    >
      {body}
    </WorksheetFundingSummaryCard>
  )
}

export default ProductsLoanFundingSummary
